import { ChevronDown, ChevronUp } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { HistoryButton } from '@/features/audit-log';
import { ApiError } from '@/shared/api/api-error';
import { ErrorState } from '@/shared/components/error-state';
import { SearchInput } from '@/shared/components/search-input';
import { StatusBadge } from '@/shared/components/status-badge';

import { useBrandOrder, useSaveBrandOrder } from '../api/brands.queries';
import type { BrandOrderItem } from '../types/brand.types';
import { OrderReason } from '@/shared/components/order-reason';
import {
  SortableOrderList,
  type SortableOrderListHandle,
} from '@/shared/components/sortable-order-list';

const LIST_ID = 'brand-order';
const ANY_CATEGORY = 'any';

/**
 * The storefront brand order (catalogue.manage, all-country scope). One global
 * position per brand: the storefront shows brands in this order under "All"
 * and inside each category tab. The category order only orders the tabs.
 *
 * The list is shown exactly as the backend returns it. Search and the category
 * picker only find, highlight and jump to brands — they never hide any, so a
 * move always means the same thing in the whole list. Saving sends every brand
 * id in the new order; the backend validates, numbers them 1..n and audits it.
 * Mount only while open.
 */
export function BrandOrderDialog({ onClose }: { onClose: () => void }) {
  const current = useBrandOrder(true);
  // Owned here, not by the editor, so a refusal stays on screen after the
  // stale list is reloaded and the editor starts afresh.
  const save = useSaveBrandOrder();

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-[90vh] flex-col gap-3 sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-1">
            <DialogTitle className="text-base">Storefront brand order</DialogTitle>
            <HistoryButton
              entityType="BrandOrder"
              entityId="storefront"
              name="the storefront brand order"
            />
          </div>
          <DialogDescription className="text-xs">
            The order customers see brands in on the storefront, in every country: under
            &quot;All&quot; and inside each category tab. The category order (set on Categories)
            only orders the tabs.
          </DialogDescription>
        </DialogHeader>
        {current.isLoading ? (
          <Skeleton className="flex-1" />
        ) : current.error || !current.data ? (
          <ErrorState error={current.error} onRetry={() => void current.refetch()} />
        ) : (
          <BrandOrderEditor
            // A refetch (e.g. after a refused, stale save) starts the editor afresh.
            key={current.dataUpdatedAt}
            initial={current.data}
            save={save}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function BrandOrderEditor({
  initial,
  save,
  onClose,
}: {
  initial: BrandOrderItem[];
  save: ReturnType<typeof useSaveBrandOrder>;
  onClose: () => void;
}) {
  const [order, setOrder] = useState(initial);
  const [reason, setReason] = useState('');
  const [text, setText] = useState('');
  const [categoryId, setCategoryId] = useState<string>(ANY_CATEGORY);
  const [matchIndex, setMatchIndex] = useState(0);
  const listRef = useRef<SortableOrderListHandle>(null);

  const originalIndex = useMemo(
    () => new Map(initial.map((brand, index) => [brand.id, index])),
    [initial],
  );
  // Categories in the order they first appear in the list — not re-sorted.
  const categories = useMemo(() => {
    const seen = new Map<number, string>();
    for (const brand of initial) {
      if (!seen.has(brand.categoryId)) {
        seen.set(brand.categoryId, brand.categoryNameEn ?? `Category ${brand.categoryId}`);
      }
    }
    return [...seen.entries()];
  }, [initial]);

  const matchesOf = (find: string, category: string) => {
    const needle = find.trim().toLowerCase();
    if (!needle && category === ANY_CATEGORY) return [];
    return order
      .filter(
        (brand) =>
          (category === ANY_CATEGORY || String(brand.categoryId) === category) &&
          (!needle ||
            brand.name.toLowerCase().includes(needle) ||
            (brand.arName ?? '').toLowerCase().includes(needle)),
      )
      .map((brand) => brand.id);
  };
  const matches = matchesOf(text, categoryId);
  const highlighted = new Set(matches);

  const jumpTo = (ids: number[], index: number) => {
    const id = ids[index];
    if (id === undefined) return;
    setMatchIndex(index);
    // The row may not be rendered yet (the list is virtualized), so scroll by index.
    listRef.current?.scrollToItem(id);
  };
  const find = (nextText: string, nextCategory: string) => {
    setText(nextText);
    setCategoryId(nextCategory);
    jumpTo(matchesOf(nextText, nextCategory), 0);
  };
  const step = (by: 1 | -1) =>
    jumpTo(matches, (matchIndex + by + matches.length) % Math.max(matches.length, 1));

  const moved = order.filter((brand, index) => originalIndex.get(brand.id) !== index).length;
  const unchanged = moved === 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 text-xs">
      <div className="flex flex-wrap items-center gap-2" role="search" aria-label="Find brands">
        <SearchInput
          value={text}
          onChange={(value) => find(value, categoryId)}
          placeholder="Find a brand…"
          className="w-full sm:w-56"
        />
        <Select value={categoryId} onValueChange={(value) => find(text, value)}>
          <SelectTrigger className="h-8 w-full text-xs sm:w-48" aria-label="Find by category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_CATEGORY} className="text-xs">
              Any category
            </SelectItem>
            {categories.map(([id, name]) => (
              <SelectItem key={id} value={String(id)} className="text-xs">
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(text.trim() || categoryId !== ANY_CATEGORY) && (
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground tabular-nums" aria-live="polite">
              {matches.length === 0
                ? 'No matches'
                : `${Math.min(matchIndex + 1, matches.length)} of ${matches.length}`}
            </span>
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              disabled={matches.length < 2}
              onClick={() => step(-1)}
              aria-label="Previous match"
            >
              <ChevronUp className="size-4" aria-hidden />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              disabled={matches.length < 2}
              onClick={() => step(1)}
              aria-label="Next match"
            >
              <ChevronDown className="size-4" aria-hidden />
            </Button>
          </div>
        )}
      </div>

      <SortableOrderList
        ref={listRef}
        className="min-h-0 flex-1"
        listId={LIST_ID}
        label="Storefront brand order"
        items={order}
        onChange={setOrder}
        originalIndex={originalIndex}
        itemLabel={(brand) => brand.name}
        highlighted={highlighted}
        disabled={save.isPending}
        renderItem={(brand) => (
          <>
            <span className="truncate font-medium">{brand.name}</span>
            <span className="text-muted-foreground truncate">
              {brand.categoryNameEn ?? `Category ${brand.categoryId}`}
            </span>
            {!brand.isActive && <StatusBadge variant="neutral">Inactive</StatusBadge>}
          </>
        )}
      />

      <OrderReason id="brand-order-reason" value={reason} onChange={setReason} />
      {save.error instanceof ApiError && (
        <p role="alert" className="text-destructive">
          {save.error.message}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <span className="text-muted-foreground me-auto" aria-live="polite">
          {unchanged ? 'No changes yet.' : `${moved} brand${moved === 1 ? '' : 's'} moved.`}
        </span>
        <Button type="button" size="sm" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={unchanged || reason.trim().length < 3 || save.isPending}
          onClick={() =>
            save.mutate(
              { brandIds: order.map((brand) => brand.id), reason: reason.trim() },
              { onSuccess: onClose },
            )
          }
        >
          Save order
        </Button>
      </div>
    </div>
  );
}
